import { useState } from 'react'
import type { ResourceAction, SheetResource } from '@rollcall/contracts'
import { useT } from './i18n-context.js'
import { resourceName } from './labels.js'

/**
 * Счётчики ресурсов на листе (D56).
 *
 * Счётчик стоит там, где живёт его особенность, а не общим списком
 * в стороне: ярость видна под «Rage», и искать её по листу
 * не приходится. Своими счётчиками распоряжается отдельная карточка
 * внизу — у них особенности нет вовсе.
 *
 * Ни одного расчёта здесь нет: сколько осталось, посчитал сервер
 * свёрткой журнала. Кнопки шлют команду и показывают то, что пришло
 * в ответ.
 */

export type ResourceRowProps = {
  readonly resource: SheetResource
  readonly onAction?: ((action: ResourceAction) => void) | undefined
  readonly busy?: boolean | undefined
}

/** Подпись восстановления: правило берётся из записи, а не из кода. */
export function recoveryLabel(
  t: ReturnType<typeof useT>,
  resource: SheetResource,
): string {
  const rule = resource.recovery
  if (rule.kind === 'none') return t('resources.recovery.none')
  if (rule.kind === 'long_rest') return t('resources.recovery.long')
  return rule.uses === null
    ? t('resources.recovery.short')
    : t('resources.recovery.shortSome', { uses: rule.uses })
}

export function ResourceRow({ resource, onAction, busy = false }: ResourceRowProps) {
  const t = useT()
  const send = (kind: 'spend' | 'restore') => () => {
    onAction?.({ kind, resource: resource.id, amount: 1 })
  }

  return (
    <div className="app-resource" data-testid={`resource-${resource.id}`}>
      <div className="app-resource-head">
        <span className="app-resource-name">{resourceName(t, resource)}</span>
        <span
          className="app-resource-count"
          data-testid={`resource-count-${resource.id}`}
        >
          {t('resources.count', { current: resource.current, max: resource.max })}
        </span>
      </div>
      <p className="app-resource-note">{recoveryLabel(t, resource)}</p>
      {onAction !== undefined && (
        <div className="app-resource-actions">
          <button
            type="button"
            className="app-button"
            disabled={busy || resource.current === 0}
            onClick={send('spend')}
            data-testid={`resource-spend-${resource.id}`}
          >
            {t('resources.spend')}
          </button>
          <button
            type="button"
            className="app-button app-button-quiet"
            disabled={busy || resource.current >= resource.max}
            onClick={send('restore')}
            data-testid={`resource-restore-${resource.id}`}
          >
            {t('resources.restore')}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Свои счётчики (D56): те, у которых записи-хозяйки нет.
 *
 * Форма заведения стоит рядом с ними, а не в настройках: счётчик
 * заводят в ту минуту, когда он понадобился за столом.
 */
export function OwnResources({
  resources,
  onAction,
  busy = false,
}: {
  readonly resources: readonly SheetResource[]
  readonly onAction?: ((action: ResourceAction) => void) | undefined
  readonly busy?: boolean | undefined
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [max, setMax] = useState('1')
  const [recovery, setRecovery] = useState<'none' | 'long_rest' | 'short_rest'>(
    'long_rest',
  )

  const own = resources.filter((item) => item.source === null)
  if (onAction === undefined && own.length === 0) return null

  const add = () => {
    const amount = Number.parseInt(max, 10)
    if (name.trim() === '' || !Number.isFinite(amount) || amount < 1) return
    onAction?.({ kind: 'add', name: name.trim(), max: amount, recovery })
    setName('')
    setMax('1')
    setOpen(false)
  }

  return (
    <section aria-labelledby="sheet-own-resources">
      <h3 id="sheet-own-resources">{t('resources.own')}</h3>
      <div className="app-list">
        {own.map((resource) => (
          <div key={resource.id} className="app-own-resource">
            <ResourceRow resource={resource} onAction={onAction} busy={busy} />
            {onAction !== undefined && (
              <button
                type="button"
                className="app-button app-button-quiet"
                disabled={busy}
                onClick={() => {
                  onAction({ kind: 'remove', resource: resource.id })
                }}
                data-testid={`resource-remove-${resource.id}`}
              >
                {t('resources.remove')}
              </button>
            )}
          </div>
        ))}
      </div>

      {onAction !== undefined &&
        (open ? (
          <div className="app-form" data-testid="resource-form">
            <label className="app-field">
              <span>{t('resources.name')}</span>
              <input
                type="text"
                value={name}
                maxLength={60}
                onChange={(event) => {
                  setName(event.target.value)
                }}
                data-testid="resource-name"
              />
            </label>
            <label className="app-field">
              <span>{t('resources.max')}</span>
              <input
                type="number"
                min={1}
                max={999}
                value={max}
                onChange={(event) => {
                  setMax(event.target.value)
                }}
                data-testid="resource-max"
              />
            </label>
            <label className="app-field">
              <span>{t('resources.recoveryLabel')}</span>
              <select
                value={recovery}
                onChange={(event) => {
                  setRecovery(event.target.value as typeof recovery)
                }}
                data-testid="resource-recovery"
              >
                <option value="long_rest">{t('resources.recovery.long')}</option>
                <option value="short_rest">{t('resources.recovery.short')}</option>
                <option value="none">{t('resources.recovery.none')}</option>
              </select>
            </label>
            <div className="app-actions">
              <button
                type="button"
                className="app-button"
                disabled={busy || name.trim() === ''}
                onClick={add}
                data-testid="resource-add"
              >
                {t('resources.add')}
              </button>
              <button
                type="button"
                className="app-button app-button-quiet"
                onClick={() => {
                  setOpen(false)
                }}
              >
                {t('resources.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="app-actions">
            <button
              type="button"
              className="app-button"
              onClick={() => {
                setOpen(true)
              }}
              data-testid="resource-open"
            >
              {t('resources.addOpen')}
            </button>
          </div>
        ))}
    </section>
  )
}
